import React, { useCallback, useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import axios from "axios";

const deployApi = axios.create({ baseURL: "", withCredentials: true });

// ── Config ─────────────────────────────────────────────────────────────────

const NODE_COLOR = {
  project:     "#6366f1",
  ec2:         "#f59e0b",
  rds:         "#3b82f6",
  elasticache: "#06b6d4",
  s3:          "#8b5cf6",
  cloudfront:  "#ec4899",
  route53:     "#10b981",
  ses:         "#f97316",
  alb:         "#64748b",
  cicd:        "#a78bfa",
  cron:        "#34d399",
  asg:         "#fbbf24",
  preview:     "#94a3b8",
  domain:      "#22d3ee",
};

const NODE_ICON = {
  project:     "⬡",
  ec2:         "⚙",
  rds:         "🗄",
  elasticache: "⚡",
  s3:          "🪣",
  cloudfront:  "🌐",
  route53:     "🔗",
  ses:         "✉",
  alb:         "⚖",
  cicd:        "🔄",
  cron:        "⏱",
  asg:         "↕",
  preview:     "👁",
  domain:      "🏷",
};

const SERVICE_LABEL = {
  project:     "Project",
  ec2:         "EC2",
  rds:         "RDS",
  elasticache: "ElastiCache",
  s3:          "S3",
  cloudfront:  "CloudFront",
  route53:     "Route 53",
  ses:         "SES",
  alb:         "Load Balancer",
  cicd:        "CI/CD",
  cron:        "Scheduler",
  asg:         "Auto Scaling",
  preview:     "Preview Env",
  domain:      "Custom Domain",
};

const STATUS_COLOR = {
  healthy:      "#22c55e",
  active:       "#22c55e",
  deployed:     "#22c55e",
  degraded:     "#f59e0b",
  pending:      "#f59e0b",
  provisioning: "#f59e0b",
  recovering:   "#f59e0b",
  critical:     "#ef4444",
  failed:       "#ef4444",
  stopped:      "#94a3b8",
  destroyed:    "#94a3b8",
};

function nodeRadius(d) {
  return d.type === "project" ? 28 : 20;
}

// ── D3 graph ───────────────────────────────────────────────────────────────

function InfraGraph({ nodes, edges, onNodeClick }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current || !nodes.length) return;
    const el = svgRef.current;
    const W = el.clientWidth || 900;
    const H = 520;

    d3.select(el).selectAll("*").remove();

    const svg = d3.select(el)
      .attr("viewBox", `0 0 ${W} ${H}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    const g = svg.append("g");

    svg.call(
      d3.zoom()
        .scaleExtent([0.25, 3])
        .on("zoom", e => g.attr("transform", e.transform))
    );

    const nodesCopy = nodes.map(d => ({ ...d }));
    const edgesCopy = edges.map(e => ({ ...e }));

    const sim = d3.forceSimulation(nodesCopy)
      .force("link", d3.forceLink(edgesCopy).id(d => d.id).distance(110).strength(0.5))
      .force("charge", d3.forceManyBody().strength(-350))
      .force("center", d3.forceCenter(W / 2, H / 2))
      .force("collide", d3.forceCollide().radius(d => nodeRadius(d) + 18));

    const link = g.append("g")
      .selectAll("line")
      .data(edgesCopy)
      .join("line")
      .attr("stroke", "#1e293b")
      .attr("stroke-width", 1.5);

    const linkLabel = g.append("g")
      .selectAll("text")
      .data(edgesCopy.filter(e => e.label))
      .join("text")
      .attr("text-anchor", "middle")
      .attr("fill", "#334155")
      .attr("font-size", "9px")
      .text(d => d.label);

    const node = g.append("g")
      .selectAll("g")
      .data(nodesCopy)
      .join("g")
      .attr("cursor", "pointer")
      .call(
        d3.drag()
          .on("start", (e, d) => { if (!e.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
          .on("drag",  (e, d) => { d.fx = e.x; d.fy = e.y; })
          .on("end",   (e, d) => { if (!e.active) sim.alphaTarget(0); d.fx = null; d.fy = null; })
      )
      .on("click", (_, d) => onNodeClick(d));

    node.append("circle")
      .attr("r", nodeRadius)
      .attr("fill", d => NODE_COLOR[d.type] || "#64748b")
      .attr("fill-opacity", 0.85)
      .attr("stroke", d => STATUS_COLOR[d.status] || "#334155")
      .attr("stroke-width", 2);

    node.append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "central")
      .attr("font-size", d => d.type === "project" ? "16px" : "13px")
      .attr("pointer-events", "none")
      .text(d => NODE_ICON[d.type] || "?");

    node.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", d => nodeRadius(d) + 14 + "px")
      .attr("fill", "#e2e8f0")
      .attr("font-size", "11px")
      .attr("font-weight", d => d.type === "project" ? "600" : "400")
      .attr("pointer-events", "none")
      .text(d => d.label.length > 16 ? d.label.substring(0, 14) + "…" : d.label);

    node.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", d => nodeRadius(d) + 26 + "px")
      .attr("fill", "#22c55e")
      .attr("font-size", "9px")
      .attr("pointer-events", "none")
      .text(d => d.monthlyEstimateEur > 0 ? `€${d.monthlyEstimateEur.toFixed(2)}/mo` : "");

    sim.on("tick", () => {
      link
        .attr("x1", d => d.source.x).attr("y1", d => d.source.y)
        .attr("x2", d => d.target.x).attr("y2", d => d.target.y);
      linkLabel
        .attr("x", d => (d.source.x + d.target.x) / 2)
        .attr("y", d => (d.source.y + d.target.y) / 2 - 4);
      node.attr("transform", d => `translate(${d.x},${d.y})`);
    });

    return () => sim.stop();
  }, [nodes, edges, onNodeClick]);

  return (
    <svg
      ref={svgRef}
      style={{ width: "100%", height: "520px", background: "#0a0a0a", display: "block" }}
    />
  );
}

// ── Legend ─────────────────────────────────────────────────────────────────

function Legend({ types }) {
  if (!types.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-3">
      {types.map(t => (
        <span key={t} className="flex items-center gap-1.5 text-xs text-silver-medium">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: NODE_COLOR[t] || "#64748b" }}
          />
          {SERVICE_LABEL[t] || t}
        </span>
      ))}
    </div>
  );
}

// ── Node detail panel ──────────────────────────────────────────────────────

function NodePanel({ node, onClose }) {
  if (!node) return null;
  return (
    <div className="absolute top-3 right-3 w-60 bg-slate-800 border border-slate-700 rounded-xl p-4 z-10 shadow-2xl">
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ background: NODE_COLOR[node.type] || "#64748b" }}
            />
            <span className="font-semibold text-silver-light text-sm">{node.label}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{SERVICE_LABEL[node.type] || node.type}</div>
        </div>
        <button
          onClick={onClose}
          className="text-slate-500 hover:text-slate-300 text-lg leading-none bg-transparent border-none cursor-pointer p-0 ml-2"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {node.sublabel && (
        <div className="text-xs text-slate-400 mb-3 break-all">{node.sublabel}</div>
      )}

      <div className="flex flex-col gap-1.5 text-xs">
        <PanelRow label="Status">
          <span style={{ color: STATUS_COLOR[node.status] || "#94a3b8" }}>
            ● {node.status || "—"}
          </span>
        </PanelRow>
        {node.hourlyEur > 0 && (
          <PanelRow label="€/hr">
            <span className="font-mono">€{node.hourlyEur.toFixed(4)}</span>
          </PanelRow>
        )}
        {node.monthlyEstimateEur > 0 && (
          <PanelRow label="Est. monthly">
            <span className="font-mono text-green-400">€{node.monthlyEstimateEur.toFixed(2)}</span>
          </PanelRow>
        )}
        {node.mtdEstimateEur > 0 && (
          <PanelRow label="MTD estimate">
            <span className="font-mono text-silver-light">€{node.mtdEstimateEur.toFixed(2)}</span>
          </PanelRow>
        )}
        {node.awsId && (
          <PanelRow label="AWS ID">
            <span className="text-slate-500 break-all">{node.awsId}</span>
          </PanelRow>
        )}
      </div>

      {node.url && (
        <a
          href={node.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-3 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-md text-xs text-center no-underline transition-colors"
        >
          Open ↗
        </a>
      )}
    </div>
  );
}

function PanelRow({ label, children }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-silver-light text-right">{children}</span>
    </div>
  );
}

// ── Summary cards ──────────────────────────────────────────────────────────

function SummaryCard({ label, value, unit, green }) {
  return (
    <div className="bg-black border border-gray-800 rounded-lg p-3">
      <div className="text-xs text-silver-dark mb-1">{label}</div>
      <div className={`font-mono font-bold text-base ${green ? "text-green-500" : "text-silver-light"}`}>
        {value}
      </div>
      {unit && <div className="text-xs text-silver-dark mt-0.5">{unit}</div>}
    </div>
  );
}

// ── Cost table ─────────────────────────────────────────────────────────────

function CostTable({ costSummary }) {
  if (!costSummary) return null;
  const {
    byService,
    totalHourlyEur,
    totalMonthlyEstimateEur,
    totalMtdEstimateEur,
    ceConfirmedMtdEur,
    ceBilledMtdEur,
    ceFetchedAt,
  } = costSummary;

  const rows = Object.entries(byService || {})
    .filter(([, v]) => v.monthlyEstimateEur > 0 || v.count > 0)
    .sort((a, b) => (b[1].monthlyEstimateEur || 0) - (a[1].monthlyEstimateEur || 0));

  return (
    <div>
      <h3 className="text-xs font-bold text-silver-dark uppercase tracking-widest mb-3">
        Cost Breakdown
      </h3>
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full text-sm min-w-[480px]">
          <thead>
            <tr className="border-b border-gray-800">
              {["Service", "Resources", "€/hr", "Est. Monthly (€)", "MTD Est. (€)"].map(h => (
                <th
                  key={h}
                  className={`pb-2 px-3 text-silver-dark font-semibold text-xs ${
                    h === "Service" || h === "Resources" ? "text-left" : "text-right"
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([svc, v]) => (
              <tr key={svc} className="border-b border-gray-900">
                <td className="py-2 px-3 text-silver-light">
                  <span className="flex items-center gap-1.5">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ background: NODE_COLOR[svc] || "#64748b" }}
                    />
                    {SERVICE_LABEL[svc] || svc}
                  </span>
                </td>
                <td className="py-2 px-3 text-right text-silver-medium">{v.count}</td>
                <td className="py-2 px-3 text-right text-silver-medium font-mono">
                  {v.hourlyEur.toFixed(4)}
                </td>
                <td className={`py-2 px-3 text-right font-mono font-semibold ${
                  v.monthlyEstimateEur > 0 ? "text-silver-light" : "text-slate-600"
                }`}>
                  {v.monthlyEstimateEur > 0 ? v.monthlyEstimateEur.toFixed(2) : "—"}
                </td>
                <td className="py-2 px-3 text-right text-silver-medium font-mono">
                  {v.mtdEstimateEur > 0 ? v.mtdEstimateEur.toFixed(2) : "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 px-3 text-center text-silver-dark text-sm">
                  No billable resources yet
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-800">
              <td className="pt-2 px-3 text-silver-medium font-bold text-sm">Total</td>
              <td />
              <td className="pt-2 px-3 text-right text-silver-medium font-mono text-sm">
                {totalHourlyEur.toFixed(4)}
              </td>
              <td className="pt-2 px-3 text-right text-green-500 font-mono font-bold text-sm">
                €{totalMonthlyEstimateEur.toFixed(2)}
              </td>
              <td className="pt-2 px-3 text-right text-green-500 font-mono font-bold text-sm">
                €{totalMtdEstimateEur.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {(ceConfirmedMtdEur != null || ceBilledMtdEur != null) && (
        <div className="mt-3 p-3 bg-slate-900 rounded-lg flex flex-wrap gap-6">
          <div>
            <div className="text-xs text-silver-dark mb-0.5">
              AWS Cost Explorer MTD (confirmed)
            </div>
            <div className="font-mono text-green-500 font-bold text-sm">
              {ceConfirmedMtdEur != null ? `€${ceConfirmedMtdEur.toFixed(2)}` : "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-silver-dark mb-0.5">
              Billed to customer MTD (+ margin)
            </div>
            <div className="font-mono text-silver-light font-bold text-sm">
              {ceBilledMtdEur != null ? `€${ceBilledMtdEur.toFixed(2)}` : "—"}
            </div>
          </div>
          {ceFetchedAt && (
            <div className="ml-auto text-right">
              <div className="text-xs text-silver-dark">CE last synced</div>
              <div className="text-xs text-silver-dark">
                {new Date(ceFetchedAt).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main section ───────────────────────────────────────────────────────────

export default function DeployInfraSection() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [graphData, setGraphData] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await deployApi.get("/api/deploy/infrastructure/graph");
      if (data.success) {
        setGraphData(data);
        setLastUpdated(data.lastUpdated);
      } else {
        setError(data.error || "Failed to load infrastructure data");
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Network error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const nodes = graphData?.graph?.nodes || [];
  const edges = graphData?.graph?.edges || [];
  const nodeTypes = [...new Set(nodes.map(n => n.type))].sort();

  return (
    <section className="bg-off-black border border-gray-800 rounded-lg p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="text-xl font-medium mb-1">Infrastructure Map</h2>
          <p className="text-silver-medium text-sm">
            Your AWS stack — drag nodes, scroll to zoom, click for cost details
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {lastUpdated && (
            <span className="text-xs text-silver-dark">
              Updated {new Date(lastUpdated).toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="bg-black border border-gray-700 text-silver-light hover:bg-gray-900 px-3 py-1.5 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-950/50 border border-red-900 rounded-lg px-4 py-3 text-sm text-red-300 mb-4">
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !graphData && (
        <div className="h-36 bg-black border border-gray-800 rounded-xl flex items-center justify-center text-silver-dark text-sm">
          Building infrastructure map…
        </div>
      )}

      {/* Empty state */}
      {!loading && nodes.length === 0 && !error && (
        <div className="bg-black border border-gray-800 rounded-xl p-8 text-center">
          <div className="text-3xl mb-3">🏗</div>
          <div className="text-silver-medium text-sm">No deployed projects yet.</div>
          <div className="text-xs text-silver-dark mt-1">
            Deploy your first app via the MCP to see the infrastructure map.
          </div>
        </div>
      )}

      {/* Graph + cost */}
      {nodes.length > 0 && (
        <div className="space-y-4">
          <Legend types={nodeTypes} />

          <div className="relative rounded-xl overflow-hidden border border-gray-800">
            <InfraGraph nodes={nodes} edges={edges} onNodeClick={setSelectedNode} />
            <NodePanel node={selectedNode} onClose={() => setSelectedNode(null)} />
            <div className="absolute bottom-2 left-3 text-xs text-slate-700 pointer-events-none select-none">
              Drag to move · Scroll to zoom · Click node for details
            </div>
          </div>

          {/* Summary cards */}
          {graphData?.costSummary && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <SummaryCard
                label="Projects"
                value={nodes.filter(n => n.type === "project").length}
              />
              <SummaryCard
                label="Total Resources"
                value={nodes.filter(n => n.type !== "project").length}
              />
              <SummaryCard
                label="Est. Hourly"
                value={`€${graphData.costSummary.totalHourlyEur.toFixed(4)}`}
                unit="/hr"
                green
              />
              <SummaryCard
                label="Est. Monthly"
                value={`€${graphData.costSummary.totalMonthlyEstimateEur.toFixed(2)}`}
                unit="/mo"
                green
              />
              <SummaryCard
                label="MTD Estimate"
                value={`€${graphData.costSummary.totalMtdEstimateEur.toFixed(2)}`}
                unit="so far"
              />
              {graphData.costSummary.ceConfirmedMtdEur != null && (
                <SummaryCard
                  label="CE Confirmed MTD"
                  value={`€${graphData.costSummary.ceConfirmedMtdEur.toFixed(2)}`}
                  unit="AWS actual"
                  green
                />
              )}
            </div>
          )}

          {/* Cost breakdown table */}
          <div className="bg-black border border-gray-800 rounded-lg p-4 sm:p-5">
            <CostTable costSummary={graphData?.costSummary} />
          </div>

          {/* Per-project */}
          <div className="bg-black border border-gray-800 rounded-lg p-4 sm:p-5">
            <h3 className="text-xs font-bold text-silver-dark uppercase tracking-widest mb-3">
              Per-Project Cost
            </h3>
            {nodes.filter(n => n.type === "project").map((p, i, arr) => (
              <div
                key={p.id}
                className={`flex justify-between items-center py-2 text-sm ${
                  i < arr.length - 1 ? "border-b border-gray-900" : ""
                }`}
              >
                <div className="min-w-0 mr-3">
                  <span className="font-semibold text-silver-light">{p.label}</span>
                  {p.sublabel && (
                    <span className="text-silver-dark ml-2 text-xs">{p.sublabel}</span>
                  )}
                  <span
                    className="ml-2 text-xs"
                    style={{ color: STATUS_COLOR[p.status] || "#94a3b8" }}
                  >
                    ● {p.status}
                  </span>
                </div>
                <div className="text-right shrink-0">
                  <span className="font-mono text-green-500 font-semibold">
                    €{p.monthlyEstimateEur.toFixed(2)}/mo
                  </span>
                  <span className="font-mono text-silver-dark text-xs ml-3">
                    €{p.mtdEstimateEur.toFixed(2)} MTD
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
